/**
 * TASK-TGE-004: Hex Materialization Effect
 *
 * Progressive reveal animation for hexes with edge-by-edge appearance and scan-line fill.
 * Creates a digital "rendering in" effect when hovering over hexes during deployment.
 */

import * as PIXI from 'pixi.js';
import type { GridEffectsConfig } from '../../types/terrain';

/**
 * Ease-out quadratic easing function
 * Creates a smooth deceleration curve for animations
 */
function easeOutQuad(t: number): number {
  return t * (2 - t);
}

/**
 * Render the materialization effect for a hex
 * Draws progressive edge reveal with staggered timing and optional scan-line fill
 *
 * @param graphics - PIXI.Graphics instance to draw on
 * @param hexCenter - Center point of the hex
 * @param corners - Array of 6 corner points for the hex
 * @param progress - Animation progress (0-1)
 * @param config - Grid effects configuration
 */
export function renderMaterializationEffect(
  graphics: PIXI.Graphics,
  hexCenter: { x: number; y: number },
  corners: { x: number; y: number }[],
  progress: number,
  config: GridEffectsConfig
): void {
  const { glowColor } = config;

  // Clear previous frame
  graphics.clear();

  // Apply easing to progress for smooth animation
  const visibleProgress = easeOutQuad(progress);

  // Draw each edge with staggered appearance
  for (let i = 0; i < 6; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % 6];

    // Stagger edge appearance: each edge starts appearing when progress reaches i/6
    // Each edge takes 1.5 "units" to fully appear, creating overlap
    const edgeProgress = Math.max(0, Math.min(1, (progress * 6 - i) / 1.5));

    if (edgeProgress > 0) {
      // Calculate the visible portion of this edge
      const midPoint = {
        x: start.x + (end.x - start.x) * edgeProgress,
        y: start.y + (end.y - start.y) * edgeProgress,
      };

      // Outer glow layer
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(midPoint.x, midPoint.y);
      graphics.stroke({
        color: glowColor,
        alpha: 0.5 * visibleProgress,
        width: 4,
        cap: 'round',
      });

      // Core bright line
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(midPoint.x, midPoint.y);
      graphics.stroke({
        color: 0xffffff,
        alpha: 0.8 * visibleProgress,
        width: 2,
        cap: 'round',
      });
    }
  }

  // Add scan-line fill pattern when progress > 30%
  if (progress > 0.3) {
    const fillAlpha = ((progress - 0.3) / 0.7) * 0.15; // Max 15% opacity
    drawHexFill(graphics, corners, glowColor, fillAlpha);
  }
}

/**
 * Draw a subtle fill inside the hex
 * Used for the scan-line effect at higher progress values
 *
 * @param graphics - PIXI.Graphics instance to draw on
 * @param corners - Array of 6 corner points for the hex
 * @param color - Fill color
 * @param alpha - Fill opacity
 */
function drawHexFill(
  graphics: PIXI.Graphics,
  corners: { x: number; y: number }[],
  color: number,
  alpha: number
): void {
  // Draw the hex polygon
  graphics.poly(corners);
  graphics.fill({
    color,
    alpha,
  });
}
